import { useRef, useState } from 'react';
import { DeviceFrame } from '../components/DeviceFrame';
import planScreen from '../assets/Screen/plan-ready.jpg';
import navigateScreen from '../assets/Screen/navigate.jpg';
import navigateLoop from '../assets/Screen/navigate-loop.mp4';
import journeysScreen from '../assets/Screen/journeys.jpg';
import './ProductProof.css';

const CAPABILITIES = [
  {
    id: 'setup',
    label: 'Plan together',
    description: 'Pick a destination, invite your group and start when everyone is ready.',
    screen: {
      src: planScreen,
      width: 1170,
      height: 2532,
      alt: 'A Tulink trip ready to start, showing the route, the destination and four people in the convoy.',
    },
  },
  {
    id: 'navigate',
    label: 'Navigate as one',
    description: 'Turn-by-turn directions and the whole group on one live map.',
    screen: {
      src: navigateScreen,
      video: navigateLoop,
      width: 1170,
      height: 2532,
      alt: 'A Tulink journey in progress, with turn-by-turn directions and two members moving along the route.',
    },
  },
  {
    id: 'history',
    label: 'Go again',
    description: 'Every trip is saved, so the next one is a tap away.',
    screen: {
      src: journeysScreen,
      width: 1170,
      height: 2532,
      alt: 'The Tulink journeys list, with past trips and a Go again button on each.',
    },
  },
];

export const ProductProof = () => {
  const [activeId, setActiveId] = useState(CAPABILITIES[0].id);
  const tabRefs = useRef({});

  const activeIndex = CAPABILITIES.findIndex((capability) => capability.id === activeId);
  const active = CAPABILITIES[activeIndex] || CAPABILITIES[0];

  const focusTab = (index) => {
    const next = CAPABILITIES[(index + CAPABILITIES.length) % CAPABILITIES.length];
    setActiveId(next.id);
    const node = tabRefs.current[next.id];
    if (node) node.focus();
  };

  const onKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        focusTab(activeIndex + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        focusTab(activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusTab(0);
        break;
      case 'End':
        event.preventDefault();
        focusTab(CAPABILITIES.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <section className="tl-product" id="product" aria-labelledby="tl-product-heading">
      <div className="tl-shell">
        <div className="tl-product__intro tl-reveal">
          <p className="tl-eyebrow">The product</p>
          <h2 id="tl-product-heading">Everything your group needs to stay together.</h2>
          <p className="tl-lede">
            Plan the trip, follow the group and arrive together—all in one app.
          </p>
        </div>

        <div className="tl-product__layout">
          <div
            className="tl-product__tabs"
            role="tablist"
            aria-label="What Tulink does"
            aria-orientation="vertical"
            onKeyDown={onKeyDown}
          >
            {CAPABILITIES.map((capability) => {
              const selected = capability.id === activeId;
              return (
                <button
                  key={capability.id}
                  type="button"
                  role="tab"
                  id={`tl-tab-${capability.id}`}
                  aria-selected={selected}
                  aria-controls={`tl-panel-${capability.id}`}
                  tabIndex={selected ? 0 : -1}
                  className={`tl-product__tab${selected ? ' is-selected' : ''}`}
                  ref={(node) => {
                    tabRefs.current[capability.id] = node;
                  }}
                  onClick={() => setActiveId(capability.id)}
                >
                  <span className="tl-product__tab-label">{capability.label}</span>
                  <span className="tl-product__tab-description">{capability.description}</span>
                </button>
              );
            })}
          </div>

          <div
            className="tl-product__panel"
            role="tabpanel"
            id={`tl-panel-${active.id}`}
            aria-labelledby={`tl-tab-${active.id}`}
            tabIndex={0}
          >
            <DeviceFrame
              key={active.id}
              src={active.screen.src}
              video={active.screen.video}
              alt={active.screen.alt}
              width={active.screen.width}
              height={active.screen.height}
              className="tl-product__device"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
